import React, { useState, useEffect } from 'react';
import { BillData, BillItem } from '../types/billing';
import { pdf } from '@react-pdf/renderer';
import TaxInvoicePDF from './TaxInvoicePDF';

interface EditableTaxInvoiceProps {
  invoice: BillData;
  onSave: (invoice: BillData) => Promise<void>;
  onClose: () => void;
  isSaving?: boolean;
}

const EditableTaxInvoice: React.FC<EditableTaxInvoiceProps> = ({
  invoice,
  onSave,
  onClose,
  isSaving = false
}) => {
  const [editableInvoice, setEditableInvoice] = useState<BillData>(invoice);
  const [isEditing, setIsEditing] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  useEffect(() => {
    setEditableInvoice(invoice);
  }, [invoice]);

  const handleInputChange = (field: string, value: any) => {
    setEditableInvoice(prev => ({
      ...prev,
      [field]: value,
      updatedAt: new Date().toISOString()
    }));
  };

  const handleNestedInputChange = (parentField: string, childField: string, value: any) => {
    setEditableInvoice(prev => ({
      ...prev,
      [parentField]: {
        ...(prev[parentField as keyof BillData] as Record<string, any>),
        [childField]: value
      },
      updatedAt: new Date().toISOString()
    }));
  };

  // Round amount to 2 decimal places for intermediate calculations
  const roundAmount = (num: number): number => {
    return Math.round(num * 100) / 100;
  };

  // Round to whole number (same as bill PDF - >= 0.50 rounds up, < 0.50 rounds down)
  const roundToWhole = (num: number): number => {
    return Math.round(num);
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const updatedItems = [...editableInvoice.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    // Recalculate totalAmount if qty or rate changed
    if (field === 'qty' || field === 'rate') {
      updatedItems[index].totalAmount = roundAmount((updatedItems[index].qty || 0) * (updatedItems[index].rate || 0));
    }
    setEditableInvoice(prev => ({
      ...prev,
      items: updatedItems,
      updatedAt: new Date().toISOString()
    }));
  };

  const addItem = () => {
    const newItem: BillItem = {
      srNo: editableInvoice.items.length + 1,
      description: '',
      sac: '',
      qty: 1,
      rate: 0,
      totalAmount: 0
    };
    setEditableInvoice(prev => ({
      ...prev,
      items: [...prev.items, newItem],
      updatedAt: new Date().toISOString()
    }));
  };

  const removeItem = (index: number) => {
    if (editableInvoice.items.length > 1) {
      const updatedItems = editableInvoice.items.filter((_, i) => i !== index);
      setEditableInvoice(prev => ({
        ...prev,
        items: updatedItems,
        updatedAt: new Date().toISOString()
      }));
    }
  };

  const calculateAmounts = () => {
    const subtotal = roundAmount(editableInvoice.items.reduce((sum, item) => sum + roundAmount((item.qty || 0) * (item.rate || 0)), 0));
    const totalGstAmount = roundAmount(subtotal * (editableInvoice.gstRate || 0));
    const cgst = roundAmount(totalGstAmount / 2); // CGST is half of GST rate
    const sgst = roundAmount(totalGstAmount / 2); // SGST is half of GST rate
    const totalBeforeRound = subtotal + totalGstAmount;
    // Round final total to whole number (same as bill PDF)
    const totalAmount = roundToWhole(totalBeforeRound);

    setEditableInvoice(prev => ({
      ...prev,
      subTotal: subtotal,
      cgstAmount: cgst,
      sgstAmount: sgst,
      totalGstAmount: totalGstAmount,
      totalAmount: totalAmount,
      updatedAt: new Date().toISOString()
    }));
  };

  useEffect(() => {
    calculateAmounts();
  }, [editableInvoice.items, editableInvoice.gstRate]);

  const handleSave = async () => {
    try {
      await onSave(editableInvoice);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving invoice:', error);
    }
  };

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const doc = <TaxInvoicePDF invoice={editableInvoice} />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Tax_Invoice_${editableInvoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const formatNumber = (num: number): string => {
    return Math.round(num).toString();
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString.trim() === '' || dateString.toUpperCase() === 'N/A') {
      return 'N/A';
    }
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch {
      return dateString; // Return as-is if not a valid date
    }
  };

  const formatCurrency = (amount: number) => {
    const rounded = Math.round(amount);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(rounded);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 font-sans text-xs md:p-8">
      <div className="mx-auto max-w-6xl rounded-lg bg-white p-4 shadow-xl ring-1 ring-gray-900/5 md:p-8">
        {/* Action Buttons */}
        <div className="mb-4 flex justify-between items-center">
          <div className="flex space-x-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Edit Invoice
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 transition-colors"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setEditableInvoice(invoice);
                    setIsEditing(false);
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 transition-colors flex items-center space-x-2"
            >
              {isGeneratingPDF ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download PDF</span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>

        {/* Header */}
        <div className="mb-4 flex justify-between items-start border-b-2 border-gray-300 pb-3">
          <div className="flex-1">
            <div className="text-lg font-bold text-gray-800">{editableInvoice.companyName}</div>
            <div className="text-sm font-semibold text-gray-600">{editableInvoice.companySubtitle}</div>
            <div className="text-xs text-gray-600 mt-1">
              <div>{editableInvoice.companyAddress}</div>
              <div>Ph: {editableInvoice.companyPhone} | Email: {editableInvoice.companyEmail}</div>
              <div>GSTN: {editableInvoice.companyGSTN}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-gray-800">TAX INVOICE</div>
            <div className="text-xs text-gray-600 mt-1">
              <div>Invoice No: {isEditing ? (
                <input
                  type="text"
                  value={editableInvoice.invoiceNumber}
                  onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                  className="bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 ml-2"
                />
              ) : (
                editableInvoice.invoiceNumber
              )}</div>
              <div>Invoice Date: {isEditing ? (
                <input
                  type="date"
                  value={editableInvoice.invoiceDate}
                  onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                  className="bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 ml-2"
                />
              ) : (
                formatDate(editableInvoice.invoiceDate)
              )}</div>
              <div>Work Order No: {isEditing ? (
                <input
                  type="text"
                  value={editableInvoice.workOrderNumber}
                  onChange={(e) => handleInputChange('workOrderNumber', e.target.value)}
                  className="bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 ml-2"
                />
              ) : (
                editableInvoice.workOrderNumber
              )}</div>
              <div>Work Order Date: {isEditing ? (
                <input
                  type="text"
                  value={editableInvoice.workOrderDate}
                  onChange={(e) => handleInputChange('workOrderDate', e.target.value)}
                  className="bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 ml-2"
                  placeholder="Enter date or N/A"
                />
              ) : (
                editableInvoice.workOrderDate && editableInvoice.workOrderDate.toUpperCase() !== 'N/A' 
                  ? formatDate(editableInvoice.workOrderDate) 
                  : editableInvoice.workOrderDate || 'N/A'
              )}</div>
              <div>Period Of Work: {isEditing ? (
                <input
                  type="text"
                  value={editableInvoice.periodOfWork || ''}
                  onChange={(e) => handleInputChange('periodOfWork', e.target.value)}
                  className="bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 ml-2"
                  placeholder="e.g., Nov-25"
                />
              ) : (
                editableInvoice.periodOfWork || '-'
              )}</div>
            </div>
          </div>
        </div>

        {/* Client Information */}
        <div className="mb-4 p-3 bg-gray-50 rounded">
          <div className="text-sm font-bold text-gray-800 mb-2">To,</div>
          <div className="text-sm text-gray-700">
            {isEditing ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={editableInvoice.clientName}
                  onChange={(e) => handleInputChange('clientName', e.target.value)}
                  className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 font-bold"
                  placeholder="Client Name"
                />
                <textarea
                  value={editableInvoice.clientAddress}
                  onChange={(e) => handleInputChange('clientAddress', e.target.value)}
                  className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
                  rows={2}
                  placeholder="Client Address"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={editableInvoice.clientGSTN}
                    onChange={(e) => handleInputChange('clientGSTN', e.target.value)}
                    className="bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
                    placeholder="GSTN"
                  />
                  <input
                    type="text"
                    value={editableInvoice.clientState}
                    onChange={(e) => handleInputChange('clientState', e.target.value)}
                    className="bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
                    placeholder="State"
                  />
                </div>
              </div>
            ) : (
              <div>
                <div className="font-bold">{editableInvoice.clientName}</div>
                <div>{editableInvoice.clientAddress}</div>
                <div>GSTN: {editableInvoice.clientGSTN}</div>
                <div>State: {editableInvoice.clientState}</div>
              </div>
            )}
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-4">
          <table className="w-full border-collapse border border-gray-400">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-400 p-2 text-center text-xs font-bold w-8">Sr. No.</th>
                <th className="border border-gray-400 p-2 text-center text-xs font-bold">Description of Goods/Services</th>
                <th className="border border-gray-400 p-2 text-center text-xs font-bold w-16">SAC</th>
                <th className="border border-gray-400 p-2 text-center text-xs font-bold w-16">Qty</th>
                <th className="border border-gray-400 p-2 text-center text-xs font-bold w-20">Rate</th>
                <th className="border border-gray-400 p-2 text-center text-xs font-bold w-20">Amount</th>
              </tr>
            </thead>
            <tbody>
              {editableInvoice.items.map((item, index) => (
                <tr key={index}>
                  <td className="border border-gray-400 p-2 text-center text-xs">{index + 1}</td>
                  <td className="border border-gray-400 p-2 text-xs">
                    {isEditing ? (
                      <textarea
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 resize-none"
                        rows={2}
                      />
                    ) : (
                      item.description
                    )}
                  </td>
                  <td className="border border-gray-400 p-2 text-xs">
                    {isEditing ? (
                      <input
                        type="text"
                        value={item.sac}
                        onChange={(e) => handleItemChange(index, 'sac', e.target.value)}
                        className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
                      />
                    ) : (
                      item.sac
                    )}
                  </td>
                  <td className="border border-gray-400 p-2 text-xs">
                    {isEditing ? (
                      <input
                        type="number"
                        value={item.qty}
                        onChange={(e) => handleItemChange(index, 'qty', parseInt(e.target.value) || 0)}
                        className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
                      />
                    ) : (
                      item.qty
                    )}
                  </td>
                  <td className="border border-gray-400 p-2 text-xs">
                    {isEditing ? (
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                        className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-right"
                        step="0.01"
                      />
                    ) : (
                      formatCurrency(item.rate)
                    )}
                  </td>
                  <td className="border border-gray-400 p-2 text-xs text-right">
                    {formatCurrency(item.qty * item.rate)}
                  </td>
                </tr>
              ))}
              {isEditing && (
                <tr>
                  <td className="border border-gray-400 p-2 text-center">
                    <button
                      onClick={addItem}
                      className="text-blue-500 hover:text-blue-700 font-bold"
                      title="Add Item"
                    >
                      +
                    </button>
                  </td>
                  <td className="border border-gray-400 p-2 text-center text-gray-500" colSpan={5}>
                    Add Item
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="mb-4 flex justify-end">
          <div className="w-80">
            <div className="flex justify-between py-1 text-xs">
              <span>Sub Total:</span>
              <span>{formatCurrency(editableInvoice.subTotal)}</span>
            </div>
            <div className="flex justify-between py-1 text-xs">
              <span>CGST ({(editableInvoice.gstRate / 2).toFixed(1)}%):</span>
              <span>{formatCurrency(editableInvoice.cgstAmount)}</span>
            </div>
            <div className="flex justify-between py-1 text-xs">
              <span>SGST ({(editableInvoice.gstRate / 2).toFixed(1)}%):</span>
              <span>{formatCurrency(editableInvoice.sgstAmount)}</span>
            </div>
            <div className="flex justify-between py-1 text-xs">
              <span>Total GST ({editableInvoice.gstRate}%):</span>
              <span>{formatCurrency(editableInvoice.totalGstAmount)}</span>
            </div>
            <div className="flex justify-between py-2 text-sm font-bold border-t border-gray-400">
              <span>Total:</span>
              <span>{formatCurrency(editableInvoice.totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Tax Rates (Editable) */}
        {isEditing && (
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <div className="text-sm font-bold text-gray-800 mb-2">Tax Rates</div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">GST Rate (%)</label>
                <input
                  type="number"
                  value={editableInvoice.gstRate}
                  onChange={(e) => handleInputChange('gstRate', parseFloat(e.target.value) || 0)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">CGST and SGST will be calculated as half of this rate</p>
              </div>
            </div>
          </div>
        )}

        {/* Terms and Conditions */}
        <div className="mb-4 p-3 bg-gray-50 rounded">
          <div className="text-sm font-bold text-gray-800 mb-2">Terms and Conditions</div>
          <div className="text-xs text-gray-700">
            {isEditing ? (
              <div className="space-y-2">
                {editableInvoice.termsAndConditions.map((term, index) => (
                  <input
                    key={index}
                    type="text"
                    value={term}
                    onChange={(e) => {
                      const updatedTerms = [...editableInvoice.termsAndConditions];
                      updatedTerms[index] = e.target.value;
                      handleInputChange('termsAndConditions', updatedTerms);
                    }}
                    className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
                    placeholder={`Term ${index + 1}`}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const updatedTerms = [...editableInvoice.termsAndConditions, ''];
                    handleInputChange('termsAndConditions', updatedTerms);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-xs"
                >
                  + Add Term
                </button>
              </div>
            ) : (
              <ul className="list-disc list-inside space-y-1">
                {editableInvoice.termsAndConditions.map((term, index) => (
                  <li key={index}>{term}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Signature Section */}
        <div className="flex justify-between items-end">
          <div className="text-xs text-gray-600">
            <div>Thank you for your business!</div>
            <div>For queries, contact: {editableInvoice.companyPhone}</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-bold text-gray-800 mb-2">Authorized Signature</div>
            <div className="border-t border-dashed border-gray-400 pt-8 w-32">
              <div className="text-xs text-gray-600">
                {isEditing ? (
                  <input
                    type="text"
                    value="Authorized Signature"
                    onChange={(e) => handleInputChange('authorizedSignature', e.target.value)}
                    className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
                    placeholder="Authorized Signature"
                  />
                ) : (
                  "Authorized Signature"
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditableTaxInvoice;
